"use client";

import {
  AccuracyChip,
  Button,
  Card,
  Checkbox,
  Icon,
  Label,
  Map as MapGlyph,
  SegmentedControl,
  Select,
} from "@/components";
import {
  MATCHABLE_ROLES,
  type ColumnMapping,
  type ColumnProfile,
  type ColumnRole,
  type GeocodeMode,
  type RowCeilingResult,
} from "@/lib/ingest";
import { ROLE_LABELS, columnDisplayName } from "./labels";

export interface MatchPanelProps {
  profiles: ColumnProfile[];
  mapping: ColumnMapping;
  /** Roles the user has overridden by hand. */
  overridden: ReadonlySet<ColumnRole>;
  hasChanges: boolean;
  summary: { ready: number; attention: number };
  rowCeiling: RowCeilingResult;
  /** Unique per rendered instance (side panel vs drawer) so ids never collide. */
  idPrefix: string;
  /** The drawer supplies its own heading — hide the panel's. */
  embedded?: boolean;
  /** True while an address batch is off being geocoded (Make map shows busy). */
  busy?: boolean;
  /** True once a map is live — the action reads "Update map". */
  live?: boolean;
  onModeChange: (mode: GeocodeMode) => void;
  onRoleChange: (role: ColumnRole, column: number | null) => void;
  onToggleLabel: (column: number) => void;
  onReset: () => void;
  onMakeMap: () => void;
}

const MODE_OPTIONS: Array<{ value: GeocodeMode; label: string }> = [
  { value: "address", label: "Addresses" },
  { value: "coordinate", label: "Coordinates" },
];

/** Geocoding roles the override dropdowns offer for the active mode. */
function rolesForMode(mode: GeocodeMode): ColumnRole[] {
  return MATCHABLE_ROLES.filter((role) =>
    mode === "coordinate" ? role === "lat" || role === "lng" : role !== "label" && role !== "lat" && role !== "lng",
  );
}

/**
 * Match panel — working register (compress: space.2–4). Detected mode up top,
 * concise instructions, one override select per geocoding role with its
 * auto-detect confidence chip, and the label-column pickers. Every override
 * re-runs row validation upstream.
 */
export function MatchPanel({
  profiles,
  mapping,
  overridden,
  summary,
  rowCeiling,
  idPrefix,
  embedded = false,
  busy = false,
  live = false,
  onModeChange,
  onRoleChange,
  onToggleLabel,
  onMakeMap,
}: MatchPanelProps) {
  const roles = rolesForMode(mapping.mode);

  const rejected = rowCeiling.level === "reject";
  const canMake = !rejected && summary.ready > 0;

  return (
    <Card className="match-panel">
      <div className="match-panel__head">
        {!embedded && <h2 className="match-panel__title">Match your columns</h2>}
        <SegmentedControl<GeocodeMode>
          label="How to read each row"
          options={MODE_OPTIONS}
          value={mapping.mode}
          onChange={onModeChange}
        />
      </div>

      <div className="match-panel__body">
        <div className="match-summary">
          <div className="match-summary__top">
            <span className="match-summary__kicker">Map your data</span>
          </div>
          <p className="match-summary__instructions">
            Choose the spreadsheet column that matches each field below.
          </p>
        </div>

        <div className="match-roles">
          {roles.map((role) => {
            const selectId = `${idPrefix}-role-${role}`;
            const assigned = mapping.roles[role];
            const confidence = mapping.confidence[role];
            const samples =
              assigned !== undefined
                ? (profiles[assigned]?.sample_values ?? []).slice(0, 2).join(" · ")
                : "";
            return (
              <div key={role} className="match-role">
                <Label htmlFor={selectId}>{ROLE_LABELS[role]}</Label>
                <Select
                  id={selectId}
                  value={assigned === undefined ? "" : String(assigned)}
                  onChange={(e) =>
                    onRoleChange(role, e.target.value === "" ? null : Number(e.target.value))
                  }
                >
                  <option value="">— none —</option>
                  {profiles.map((p) => (
                    <option key={p.index} value={p.index}>
                      {columnDisplayName(p.header, p.index)}
                    </option>
                  ))}
                </Select>
                {!overridden.has(role) && assigned !== undefined && confidence ? (
                  <AccuracyChip level={confidence} />
                ) : (
                  <span aria-hidden />
                )}
                {samples !== "" && <span className="match-role__sample">e.g. {samples}</span>}
              </div>
            );
          })}
        </div>

        <div className="match-section">
          <h3 className="match-section__title">Map markers</h3>
          <p className="match-section__hint">
            Select the data shown to users when they click any point on the map.
          </p>
          <div className="match-labels">
            {profiles.map((p) => (
              <Checkbox
                key={p.index}
                checked={mapping.label_columns.includes(p.index)}
                onChange={() => onToggleLabel(p.index)}
                label={columnDisplayName(p.header, p.index)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="match-panel__foot">
        {rejected && rowCeiling.message && <p className="match-blocked">{rowCeiling.message}</p>}
        {!rejected && summary.ready === 0 && (
          <p className="match-blocked">No rows are ready yet — check the column match above.</p>
        )}
        <Button
          variant="primary"
          size="lg"
          className="match-make"
          disabled={!canMake}
          loading={busy}
          iconLeading={<Icon icon={MapGlyph} size="md" />}
          onClick={onMakeMap}
        >
          {live ? "Update map" : "Generate map"}
        </Button>
      </div>
    </Card>
  );
}
